import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "./PatientLayout.scss";

import Spinner from "../../../atoms/Spinner/Spinner";
import Button from "../../../atoms/Button/Button";

import { RootState } from "../../../store/store";
import {
  getPatientDetailsRequest,
  resetPatientDetails,
} from "../../../store/slices/PatientSlice";
import {
  getHealthCoachDetailsRequest,
  resetHealthCoachDetails,
} from "../../../store/slices/HealthCoachSlice";

import PatientSidebar from "./PatientSidebar";
import PatientHeader from "./PatientHeader";
import { PatientPageHeaderContext } from "../../../molecules/PatientDetailsV2/PageHeader/PatientPageHeaderContext";
import OverviewPage from "../OverviewPage/OverviewPage";
import RecordsPage from "../RecordsPage/RecordsPage";
import ConsultationsPage from "../ConsultationsPage/ConsultationsPage";
import VitalsPage from "../VitalsPage/VitalsPage";
import DietTaskPage from "../AssignTasks/DietTaskPage/DietTaskPage";
import DietAdherencePage from "../AssignTasks/DietAdherencePage/DietAdherencePage";
import DietReportPage from "../AssignTasks/DietReportPage/DietReportPage";
import ExercisePlanPage from "../AssignTasks/ExercisePlanPage/ExercisePlanPage";
import IncentiveSpirometerPage from "../AssignTasks/IncentiveSpirometerPage/IncentiveSpirometerPage";
import StepsTaskPage from "../AssignTasks/StepsTaskPage/StepsTaskPage";
import WaterIntakeTaskPage from "../AssignTasks/WaterIntakeTaskPage/WaterIntakeTaskPage";
import SleepTaskPage from "../AssignTasks/SleepTaskPage/SleepTaskPage";
import MainGoalsPage from "../ProgressPages/MainGoalsPage";
import CustomGoalsPage from "../ProgressPages/CustomGoalsPage";
import AssessmentRecapPage from "../ProgressPages/AssessmentRecapPage";
import SharedProfilePage from "../SharedProfile/SharedProfilePage";
import CoachAssessmentPage from "../CoachAssessment/CoachAssessmentPage";
import {
  AssessmentMode,
  AssessmentProgressProvider,
} from "../CoachAssessment/AssessmentProgressContext";
import useCoachPermissions from "../../../hooks/useCoachPermissions";
import { isPlanPaused } from "../../../utils/patient";
import PlanPausedModal from "../../../molecules/PatientDetailsV2/PlanPausedModal/PlanPausedModal";

const DietBaselineView = () => (
  <CoachAssessmentPage view="diet" mode={AssessmentMode.Baseline} />
);
const DietFollowupView = () => (
  <CoachAssessmentPage view="diet" mode={AssessmentMode.Followup} />
);
const ExerciseBaselineView = () => (
  <CoachAssessmentPage view="exercise" mode={AssessmentMode.Baseline} />
);
const ExerciseFollowupView = () => (
  <CoachAssessmentPage view="exercise" mode={AssessmentMode.Followup} />
);
const SuccessBaselineView = () => (
  <CoachAssessmentPage view="success" mode={AssessmentMode.Baseline} />
);
const SuccessFollowupView = () => (
  <CoachAssessmentPage view="success" mode={AssessmentMode.Followup} />
);

// Sidebar key -> screen. The Progress & Goals and Care Triad entries are the
// pages the old "Progress" and "Assign Tasks" tabs used to route to; they are
// rendered here unchanged now that the layout switches views itself instead of
// using a router Outlet.
const VIEWS: Record<string, React.ComponentType<any>> = {
  overview: OverviewPage,
  shared: SharedProfilePage,
  records: RecordsPage,
  labvitals: VitalsPage,
  consults: ConsultationsPage,
  "main-goals": MainGoalsPage,
  "custom-goals": CustomGoalsPage,
  "assessment-recap": AssessmentRecapPage,
  diet: DietFollowupView,
  "diet-baseline": DietBaselineView,
  exercise: ExerciseFollowupView,
  "exercise-baseline": ExerciseBaselineView,
  success: SuccessFollowupView,
  "success-baseline": SuccessBaselineView,
  "diet-plans": DietTaskPage,
  "diet-adherence": DietAdherencePage,
  "diet-reports": DietReportPage,
  "exercise-plan": ExercisePlanPage,
  "incentive-spirometer": IncentiveSpirometerPage,
  steps: StepsTaskPage,
  "water-intake": WaterIntakeTaskPage,
  sleep: SleepTaskPage,
};

const PatientLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [activeView, setActiveView] = useState("overview");
  const [sharedReload, setSharedReload] = useState(0);
  const [titleSlotEl, setTitleSlotEl] = useState<HTMLElement | null>(null);
  const titleSlotRef = useCallback((node: HTMLDivElement | null) => {
    setTitleSlotEl(node);
  }, []);

  const dispatch = useDispatch();
  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const { healthCoachDetails } = useSelector(
    (state: RootState) => state.healthCoach,
  );
  const { isCareTriadViewAllowed } = useCoachPermissions();

  useEffect(() => {
    // setIsLoading(true);
    if (params?.token) {
      const patient_id: any = params?.patientId || params?.id;
      localStorage.setItem("patient_id", patient_id);
      const health_coach_id: any = params?.healthCoachId || params?.id;
      localStorage.setItem("health_coach_id", health_coach_id);
      const token: any = params?.token;
      localStorage.setItem("token", token);

      navigate(
        `/patient-details-v2/${patient_id}/${health_coach_id}/progress/main-goals`,
      );
    } else {
      // Keep session keys in sync on refresh / deep links (no :token segment)
      if (params?.patientId) {
        localStorage.setItem("patient_id", params.patientId);
      }
      if (params?.healthCoachId) {
        localStorage.setItem("health_coach_id", params.healthCoachId);
      }
    }
  }, [location.pathname, params]);

  useEffect(() => {
    if (!isCareTriadViewAllowed(activeView)) {
      setActiveView("overview");
    }
  }, [activeView, isCareTriadViewAllowed]);

  useEffect(() => {
    if (params?.id || params?.patientId) {
      if (!patientDetails?.data && !patientDetails?.errors) {
        getPatientDetails();
        getHealthCoachDetails();
      } else if (patientDetails?.errors) {
        console.error(
          "Error while fetching patient details",
          patientDetails?.errors,
        );
      }
    }

    return () => {
      dispatch(resetPatientDetails());
      dispatch(resetHealthCoachDetails());
    };
  }, []);

  useEffect(() => {
    if (patientDetails?.data) {
      localStorage.setItem(
        "PatientInfo",
        JSON.stringify({
          ...patientDetails?.data,
          disease: (patientDetails?.data as any)?.disease || "",
          medicalCondition: patientDetails?.data?.medical_condition_name || "",
        }),
      );
    }
  }, [patientDetails]);

  useEffect(() => {
    if (patientDetails?.data && healthCoachDetails?.data) {
      setIsLoading(false);
    }
  }, [patientDetails?.data, healthCoachDetails?.data]);

  useEffect(() => {
    const data = patientDetails?.data;
    if (!data) return;
    setPauseModalOpen(isPlanPaused(data));
  }, [patientDetails?.data]);

  const getPatientDetails = () => {
    const patient_id: any = params?.patientId || params?.id;
    const health_coach_id: any = params?.healthCoachId || params?.id;
    const payload = {
      patient_id: patient_id,
      health_coach_id: health_coach_id,
    };
    new Promise((resolve, reject) => {
      dispatch(
        getPatientDetailsRequest({
          payload,
          resolve,
          reject,
        }),
      );
    });
  };

  const getHealthCoachDetails = () => {
    new Promise((resolve, reject) => {
      dispatch(
        getHealthCoachDetailsRequest({
          // payload,
          resolve,
          reject,
        }),
      );
    });
  };

  if (patientDetails?.errors || healthCoachDetails?.errors) {
    return (
      <div className="layout-container-error">
        <div className="error-msg flex-container">
          Error loading details. Please try again.
          <Button
            onClick={() => {
              // setIsLoading(true);
              // getPatientDetails();
              // getHealthCoachDetails();
              window.location.reload();
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const ActiveView = VIEWS[activeView];

  if (isLoading) {
    return (
      <div className="layout-container-loader">
        <Spinner />
      </div>
    );
  }

  return (
    <AssessmentProgressProvider
      patientId={
        patientDetails?.data?.patient_id ||
        params?.patientId ||
        params?.id ||
        null
      }
    >
      <PatientPageHeaderContext.Provider value={{ headerEl: titleSlotEl }}>
        <PlanPausedModal
          show={pauseModalOpen}
          patientName={patientDetails?.data?.name}
          planName={patientDetails?.data?.plan_name}
          onClose={() => setPauseModalOpen(false)}
        />
        <div className="patient-layout-container">
          <div className="patient-layout-sidebar">
            <PatientSidebar
              activeView={activeView}
              onNavigate={setActiveView}
              onConditionSaved={() => {
                if (activeView === "shared") {
                  setSharedReload((n) => n + 1);
                }
              }}
              patient={patientDetails?.data}
            />
          </div>

          <div className="patient-layout-main-content">
            <PatientHeader titleSlotRef={titleSlotRef} />
            <div
              className="patient-layout-content"
              key={
                activeView === "shared"
                  ? `shared-${sharedReload}`
                  : activeView
              }
            >
              {ActiveView && <ActiveView onNavigate={setActiveView} />}
            </div>
          </div>
        </div>
      </PatientPageHeaderContext.Provider>
    </AssessmentProgressProvider>
  );
};

export default PatientLayout;
